import * as React from 'react';
import { useFormState } from 'react-hook-form';

// Hook that returns a function for pulling the subset of values the
// user actually touched. Default behaviour for every Edit screen
// should be "PATCH only what changed" — sending the full snapshot
// wastes bandwidth, multiplies the surface for server-side bugs, and
// (on Rails) trips strong-parameter enforcement for fields whose
// `update` permit is intentionally narrower than `create`.
//
// Why a hook and not a plain helper: RHF wraps `formState` in a Proxy
// that *only computes* `dirtyFields` when something subscribes to it
// during render. Reading `form.formState.dirtyFields` inside a submit
// callback (after render is done) returns the default empty object,
// even when the user has changed half the form. `useFormState` is the
// canonical subscription — calling it during render arms the tracker.
//
// Returned function reads the latest `dirtyFields` and `values` at
// the moment it's called, so callers can invoke it inside their
// `handleSubmit` body and get the live snapshot.
//
// `exclude` masks fields that are tracked in the form but should
// never reach the server (e.g. UI-only modes like `operation_type`
// on indicators). The mask is applied after the dirty filter — a
// field in `exclude` is dropped whether it's dirty or not.
//
// Returns `{}` when nothing changed. Callers can short-circuit on
// `Object.keys(payload).length === 0` to skip the network round-trip
// entirely (no save, just close the form).
//
// Usage:
//
//   const form = useForm({ defaultValues, resolver, ... });
//   const getDirtyValues = useDirtyValues(form);
//
//   const onSubmit = form.handleSubmit(() => {
//       const payload = getDirtyValues({ exclude: ['operation_type'] });
//       if (Object.keys(payload).length === 0) {
//           onCancel();
//           return;
//       }
//       model.save(payload, { patch: true, ... });
//   });

export const useDirtyValues = (form) => {
    // Subscribe to dirtyFields during render so RHF's proxy actually
    // computes the dirty state on every change. The destructure is
    // load-bearing — without it the proxy never wires up the tracker.
    const { dirtyFields } = useFormState({ control: form.control });

    return React.useCallback(({ exclude = [] } = {}) => {
        const excludedSet = new Set(exclude);
        const values = form.getValues();
        const result = {};

        Object.keys(dirtyFields).forEach((key) => {
            if (!dirtyFields[key] || excludedSet.has(key)) {
                return;
            }
            result[key] = values[key];
        });

        return result;
    }, [dirtyFields, form]);
};
